// frontend/src/features/onboarding/steps/FinishStep.tsx
//
// Wizard step 4 — celebration + finish (Task 18). Marks onboarding complete and
// drops the user on their dashboard.
//
// The primary CTA awaits update({ completed: true }) so the server records the
// completion, then navigates home. If the PATCH FAILS we still navigate: the
// OnboardingGate only redirects to /welcome when tanks.length === 0, and step 1
// guaranteed a tank, so a failed completion can never re-trap the user. The
// failure is logged by the mutation's onError (see useOnboarding), not swallowed
// silently here.
import { useEffect, useRef } from 'react';
import { Button, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '@/hooks/useOnboarding';
import { trackOnboarding } from '@/lib/onboardingAnalytics';
import { useOnboardingWizard } from '../WizardContext';

export function FinishStep() {
  const navigate = useNavigate();
  const { update } = useOnboarding();
  const { state } = useOnboardingWizard();

  // Mark onboarding complete the moment the finish step MOUNTS, fire-and-forget.
  // Reaching this step means the user skipped or completed the first-value
  // action, and design §3.1 ("any skip past step 1 sets completed") requires
  // that they count as onboarded even if they abandon here without clicking the
  // CTA — backend metrics key on onboarding_completed_at. The button's awaited
  // update() below stays too; the server is idempotent (COALESCE), so the two
  // calls never conflict. The ref makes this fire exactly once per mount.
  const completedOnMount = useRef(false);
  useEffect(() => {
    if (completedOnMount.current) return;
    completedOnMount.current = true;
    update({ completed: true }).catch(() => {});
    // Funnel: reaching the finish step IS wizard completion (matches the
    // completes-on-mount semantics above). Fired exactly once per mount.
    trackOnboarding('wizard_completed');
  }, [update]);

  const handleFinish = async () => {
    try {
      await update({ completed: true });
    } catch {
      // Intentionally ignored: the OnboardingGate's tanks.length > 0 guard means
      // a failed PATCH cannot re-trap the user. useOnboarding's onError logs it.
    }
    navigate('/', { replace: true });
  };

  return (
    <Stack gap="lg" align="center" data-testid="onboarding-step-finish">
      <ThemeIcon size={72} radius="xl" variant="light" color="teal">
        <IconCircleCheck size={40} />
      </ThemeIcon>

      <Stack gap="xs" align="center">
        <Title order={1} ta="center">
          You're all set!
        </Title>
        <Text c="dimmed" ta="center" maw={420}>
          {state.connectedDevice
            ? 'Your dashboard will fill in over the next few minutes as we pull in your history. Head over and watch it come to life.'
            : "Your dashboard is ready. Log tests, track tasks, and watch your reef's trends take shape."}
        </Text>
      </Stack>

      <Button size="md" onClick={() => void handleFinish()}>
        Go to my dashboard
      </Button>
    </Stack>
  );
}
